"use client";

import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, X, Shield } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState } from "react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useSession, stopImpersonating } from "@/lib/auth/client";

interface ImpersonationBannerProps {
  className?: string;
  redirectTo?: string;
}

export function ImpersonationBanner({
  className,
  redirectTo = "/admin",
}: ImpersonationBannerProps) {
  const { data: session } = useSession();
  const router = useRouter();
  const [isEnding, setIsEnding] = useState(false);
  const queryClient = useQueryClient();

  // Check if we're in an impersonation session
  // Better Auth stores impersonatedBy in session.session.impersonatedBy
  // Type assertion needed as Better Auth types don't include custom session fields yet
  const isImpersonating = !!session?.session?.impersonatedBy;

  if (!isImpersonating) {
    return null;
  }

  const handleStopImpersonation = async () => {
    try {
      if (!session?.session?.impersonatedBy) {
        throw new Error("No impersonated user found");
      }

      setIsEnding(true);

      // Call Better Auth admin plugin to stop impersonation
      const result = await stopImpersonating();

      // TODO: Move impersonation end logging to server action
      // Cannot call server-only logImpersonationSessionEnd from client component
      // This should be implemented as a server action or API route
      // See: lib/business-events/handlers/auth/ImpersonationSessionLoggingHandler.ts

      if (!result.data) {
        throw new Error(
          result.error?.message || "Failed to stop impersonation",
        );
      }

      // Invalidate saved listings cache after impersonation stops
      queryClient.invalidateQueries({ queryKey: ["zenstack", "SavedListing"] });

      // Refresh the router to apply the new session
      if (redirectTo) {
        router.push(redirectTo);
      }
      window.location.reload();
    } catch (error) {
      console.error("Failed to stop impersonation:", error);
      window.alert("Failed to stop impersonation. Please try again.");
    } finally {
      setIsEnding(false);
    }
  };

  return (
    <Alert className={`border-orange-200 bg-orange-50 ${className}`}>
      <AlertTriangle className="h-4 w-4 text-orange-600" />
      <AlertDescription className="flex w-full items-center justify-between">
        <div className="flex flex-1 items-center gap-4">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-orange-600" />
            <span className="font-semibold text-orange-800">
              Impersonation Active
            </span>
          </div>

          <div className="text-sm text-orange-700">
            An admin is impersonating this user.
          </div>

          <div className="text-xs font-medium text-orange-600">
            All actions are being logged for audit purposes.
          </div>
        </div>

        <Button
          onClick={handleStopImpersonation}
          disabled={isEnding}
          size="sm"
          variant="outline"
          className="ml-4 border-orange-300 text-orange-700 hover:bg-orange-100"
        >
          {isEnding ? (
            "Ending..."
          ) : (
            <>
              <X className="mr-1 h-3 w-3" />
              Stop Impersonation
            </>
          )}
        </Button>
      </AlertDescription>
    </Alert>
  );
}

// Hook to check if impersonation is active
export function useImpersonation() {
  const { data: session } = useSession();

  // Type assertion needed as Better Auth types don't include custom session fields yet
  return {
    isImpersonating: !!session?.session?.impersonatedBy,
    impersonatedBy: session?.session?.impersonatedBy || null,
  };
}
